"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function TheCreativeEngine() {
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const sectionMarkers = [{
    id: "founding",
    label: "THE ORIGIN",
    year: "2015"
  }, {
    id: "vision",
    label: "THE METHOD",
    year: ""
  }, {
    id: "scale",
    label: "THE IMPACT",
    year: "TODAY"
  }];
  const editorialImages = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_moodboard_process.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_moodboard_process.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_moodboard_process.png-wvc-sizes",
    alt: "Conceptual mood board with textures and color palettes",
    caption: "FIG. 01 — CONCEPTUAL ARCHITECTURE, INITIAL PHASE"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_production_set.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_production_set.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_production_set.webp-wvc-sizes",
    alt: "High-end commercial video production set lighting",
    caption: "FIG. 02 — CINEMATIC EXECUTION, STUDIO FLOOR"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_final_delivery.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_final_delivery.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_final_delivery.png-wvc-sizes",
    alt: "Final high-end commercial video spot on monitor",
    caption: "FIG. 03 — FINAL DELIVERY, POST-PRODUCTION"
  }];
  return <section data-section-id="2929" id="the-creative-engine" className="bg-background text-foreground font-default">
      {}
      <div ref={heroRef} className={`relative w-full h-[80vh] min-h-[600px] overflow-hidden transition-opacity duration-1000 ${heroVisible ? "opacity-100" : "opacity-0"}`}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_hero_cinematic.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_hero_cinematic.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_hero_cinematic.webp-wvc-sizes" alt="Cinematic lighting setup for a high-end commercial shoot" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        {}
        <div className="absolute inset-x-0 bottom-0 h-[30%]" style={{
        background: "linear-gradient(to bottom, transparent 0%, var(--background) 100%)"
      }} />

        {}
        <div className="absolute top-8 left-6 md:top-12 md:left-12 lg:left-16 z-10">
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-foreground/80 uppercase">CREATIVE ENGINE</span>
        </div>

        {}
        <div className="absolute bottom-12 left-6 md:bottom-20 md:left-12 lg:left-16 right-6 md:right-12 z-10">
          <h2 className="font-default font-bold leading-[0.95] tracking-tight text-foreground text-[clamp(2.5rem,10vw,7.5rem)]">
            <span className="block">We craft</span>
            <span className="block italic font-normal text-primary">
              visual adrenaline
            </span>
            <span className="block">for modern brands.</span>
          </h2>
        </div>

        {}
        <div className="absolute top-8 right-6 md:top-12 md:right-12 lg:right-16 z-10 flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.2em] text-foreground/60">
            VIVID // STUDIO &nbsp;/&nbsp; PRODUCTION
          </span>
        </div>
      </div>

      {}
      <div ref={contentRef} className={`px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-40 transition-opacity duration-1000 ${contentVisible ? "opacity-100" : "opacity-0"}`}>
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="flex items-center gap-3 mb-10">
                <div className="h-px w-8 bg-border" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
                  Index
                </span>
              </div>

              <nav aria-label="Section index" className="space-y-6">
                {sectionMarkers.map((marker, i) => <Link key={marker.id} to={`#${marker.id}`} data-index={i} className="group flex items-baseline gap-4 border-t border-border pt-4 hover:border-primary transition-colors duration-300 text-foreground">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1">
                      <div className="font-mono text-xs tracking-[0.2em] text-foreground uppercase group-hover:text-primary transition-colors">
                        {marker.label}
                      </div>
                      {marker.year && <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mt-1">
                          / {marker.year}
                        </div>}
                    </div>
                  </Link>)}
              </nav>

              <div className="hidden lg:block mt-16 pt-8 border-t border-border">
                <p className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase leading-relaxed">
                  VIVID STUDIO &nbsp;/&nbsp; Creative Agency
                  <br />
                  Global Production
                </p>
              </div>
            </div>
          </aside>

          {}
          <article className="lg:col-span-7 max-w-[62ch]">
            {}
            <div id="founding" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono text-[10px] tracking-[0.25em] text-primary uppercase">
                  01 / The Origin
                </span>
                <div className="flex-1 h-px bg-border" />
                <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                  2015
                </span>
              </div>

              <h3 className="font-default text-3xl md:text-4xl font-bold leading-tight mb-8 text-foreground">
                Where strategy meets cinematic impact.
              </h3>

              <p className="text-lg leading-[1.8] text-foreground/85 mb-6">
                <span className="font-default text-6xl float-left mr-3 mt-1 leading-[0.85] text-primary">
                  V
                </span>
                IVID Studio was born from a desire to bridge the gap between
                clinical brand strategy and high-octane visual storytelling. We
                don't just make content; we build visual systems that command
                attention. From the first mood board to the final frame, our
                process is designed to turn abstract concepts into visceral
                experiences.
              </p>

              <p className="text-lg leading-[1.8] text-foreground/85">
                What began as a small collective of directors and strategists
                has evolved into a full-service production powerhouse,
                redefining how brands communicate in a saturated digital
                landscape.
              </p>
            </div>

            {}
            <figure className="my-16 -mx-6 md:-mx-12 lg:-mx-16 xl:-mx-32">
              <img src={editorialImages[0].src} data-wvc-srcset={editorialImages[0].srcset} data-wvc-sizes={editorialImages[0].sizes} alt={editorialImages[0].alt} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
              <figcaption className="mt-3 px-6 md:px-12 lg:px-16 xl:px-32 font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
                {editorialImages[0].caption}
              </figcaption>
            </figure>

            {}
            <div id="vision" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono text-[10px] tracking-[0.25em] text-primary uppercase">
                  02 / The Method
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <h3 className="font-default text-3xl md:text-4xl font-bold leading-tight mb-8 text-foreground">
                Engineering the perfect visual narrative.
              </h3>

              <p className="text-lg leading-[1.8] text-foreground/85 mb-6">
                <span className="font-default text-6xl float-left mr-3 mt-1 leading-[0.85] text-primary">
                  E
                </span>
                very project is a calibrated operation. We analyze the brand
                DNA, map the audience journey, and execute with surgical
                precision. Whether it's a high-end commercial spot or a
                social-first campaign, our team ensures that every pixel serves
                the narrative. We don't believe in filler; we believe in
                impact.
              </p>

              <p className="text-lg leading-[1.8] text-foreground/85">
                Our methodology is simple: define the core, amplify the
                message, and deliver with cinematic excellence.
              </p>
            </div>

            {}
            <figure className="my-16 -mx-6 md:-mx-12 lg:-mx-16 xl:-mx-32">
              <img src={editorialImages[1].src} data-wvc-srcset={editorialImages[1].srcset} data-wvc-sizes={editorialImages[1].sizes} alt={editorialImages[1].alt} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
              <figcaption className="mt-3 px-6 md:px-12 lg:px-16 xl:px-32 font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
                {editorialImages[1].caption}
              </figcaption>
            </figure>

            {}
            <blockquote ref={quoteRef} className={`my-24 md:my-32 transition-all duration-1000 ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-default italic font-bold leading-[1.05] tracking-tight text-foreground text-[clamp(2rem,5vw,3.5rem)]">
                <span className="text-primary">&ldquo;</span>
                We don&rsquo;t just
                <br />
                produce content.
                <br />
                We architect
                <br />
                visual adrenaline.
                <span className="text-primary">&rdquo;</span>
              </p>
              <footer className="mt-10 flex items-center gap-4">
                <div className="h-px w-12 bg-primary" />
                <cite className="not-italic font-mono text-[10px] tracking-[0.3em] text-muted-foreground uppercase">
                  Jordan Rivers &nbsp;/&nbsp; Creative Director, Founder
                </cite>
              </footer>
            </blockquote>

            {}
            <div id="scale" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono text-[10px] tracking-[0.25em] text-primary uppercase">
                  03 / The Impact
                </span>
                <div className="flex-1 h-px bg-border" />
                <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                  Today
                </span>
              </div>

              <h3 className="font-default text-3xl md:text-4xl font-bold leading-tight mb-8 text-foreground">
                Scaling stories for a global audience.
              </h3>

              <p className="text-lg leading-[1.8] text-foreground/85 mb-6">
                <span className="font-default text-6xl float-left mr-3 mt-1 leading-[0.85] text-primary">
                  T
                </span>
                oday, VIVID Studio operates at the intersection of art and
                commerce, delivering end-to-end campaigns for the world's most
                ambitious brands. From initial script treatments to full-scale
                video production and social-first content scaling, we provide
                the creative infrastructure necessary to dominate the screen.
              </p>

              <p className="text-lg leading-[1.8] text-foreground/85">
                We partner with brands that understand the value of a bold
                vision. If you're ready to elevate your creative output, let's
                start a conversation.
              </p>
            </div>

            {}
            <figure className="my-16 -mx-6 md:-mx-12 lg:-mx-16 xl:-mx-32">
              <img src={editorialImages[2].src} data-wvc-srcset={editorialImages[2].srcset} data-wvc-sizes={editorialImages[2].sizes} alt={editorialImages[2].alt} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
              <figcaption className="mt-3 px-6 md:px-12 lg:px-16 xl:px-32 font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
                {editorialImages[2].caption}
              </figcaption>
            </figure>

            {}
            <div className="mt-12 pt-8 border-t border-border grid grid-cols-3 gap-6">
              <div data-index={0}>
                <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase mb-2">
                  Campaigns
                </div>
                <div className="font-default text-2xl md:text-3xl font-bold text-foreground">
                  250+
                </div>
              </div>
              <div data-index={1}>
                <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase mb-2">
                  Global Reach
                </div>
                <div className="font-default text-2xl md:text-3xl font-bold text-foreground">
                  12M+
                </div>
              </div>
              <div data-index={2}>
                <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase mb-2">
                  Retention
                </div>
                <div className="font-default text-2xl md:text-3xl font-bold text-foreground">
                  98<span className="text-primary">%</span>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>;
}